"use client";

import { FooterCta } from "@features/cta";
import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { TFooterCta } from "@shared/cms/types";
import { cn } from "@ubud-ui/react-ui-kit";
import Image from "next/image";
import Link from "next/link";
import Markdown from "react-markdown";

export function Footer() {
  const clientConfig = useClientConfig();
  const footer = clientConfig?.layoutSetting?.footer;

  return (
    <div>
      {footer?.cta && <FooterCta {...(footer.cta as TFooterCta)} />}
      <footer className="text-gray-700 py-14 border-t border-gray-100">
        <div className="container">
          <div className="footer-head flex flex-col sm:flex-row gap-8">
            <div className="flex items-center gap-6 flex-1 flex-wrap">
              {footer?.logos &&
                footer.logos.length > 0 &&
                footer.logos.map((logo) => (
                  <Link key={logo.id} href={logo.href} target={logo.target}>
                    {logo.icon && (
                      <Image
                        src={logo.icon?.url || ""}
                        width={logo.icon?.width || 128}
                        height={logo.icon?.height || 44}
                        alt={logo.icon?.alternativeText || logo.icon?.name || ""}
                        className="w-[128px] h-auto object-contain"
                      />
                    )}
                  </Link>
                ))}
            </div>
            {footer?.navigations && footer.navigations.length > 0 && (
              <div className="flex gap-4 flex-wrap font-semibold text-base">
                {footer.navigations.map((nav) => (
                  <Link key={nav.id} className="text-secondary hover:text-primary-800" href={nav.href} target={nav.target}>
                    {nav.text}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-between gap-10 flex-col sm:flex-row mt-10">
            {footer?.footerContents &&
              footer?.footerContents?.length > 0 &&
              footer?.footerContents.map((content, index) => {
                return (
                  <div key={"footer-content-" + index} className={cn(index % 2 === 1 ? "text-left sm:text-right" : "")}>
                    <h6 className="font-bold mb-0 p-0 text-lg">{content.title}</h6>
                    <div className="mt-3 p-0">
                      <Markdown>{content.description}</Markdown>
                    </div>
                    {content.contacts && content.contacts.length > 0 && (
                      <div className={cn("flex flex-col items-start gap-5 mt-8", index % 2 === 1 ? "sm:items-end" : "")}>
                        {content.contacts.map((contact) => {
                          return (
                            <div key={contact.id}>
                              {contact.list && contact.list.length > 0 && (
                                <div className="flex items-center gap-6 flex-wrap">
                                  {contact.list.map((contactItem) => {
                                    return (
                                      <Link
                                        key={contactItem.id}
                                        href={contactItem.href}
                                        target={contactItem.target}
                                        className="inline-flex items-center"
                                      >
                                        {contactItem.icon && (
                                          <Image
                                            src={contactItem.icon?.url}
                                            alt={contactItem.icon?.alternativeText || contactItem.icon?.name}
                                            width={contactItem.icon?.width || 24}
                                            height={contactItem.icon?.height || 24}
                                            className="size-6 mr-2.5"
                                          />
                                        )}
                                        <span className="font-semibold">{contactItem.text}</span>
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {content.socials && content.socials.length > 0 && (
                      <div className={cn("flex items-center font-semibold gap-3 mt-8", index % 2 === 1 ? "sm:justify-end" : "")}>
                        {content.socials.map((social) => {
                          return (
                            <Link
                              key={social.id}
                              className="w-10 h-10 flex items-center justify-center hover:bg-gray-200 rounded-full border border-gray-200"
                              href={social.href}
                              target={social.target}
                            >
                              {social.icon && (
                                <Image
                                  src={social.icon?.url || ""}
                                  width={social.icon?.width || 20}
                                  height={social.icon?.height || 20}
                                  alt={social.icon?.alternativeText || social.icon?.name || ""}
                                  className="size-5"
                                />
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
          <div className="mt-10 flex flex-col md:flex-row md:items-center justify-center text-center text-sm">
            <div className="font-normal text-gray-500 mt-4 md:mt-0">{footer?.footnote}</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
